'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

type Props = {
  monthCursor: Date; // first of the month
  setMonthCursor: (d: Date) => void;
  selectedDate: string | null; // yyyy-mm-dd
  onSelect: (date: string) => void;
  availableDays: Set<string>; // yyyy-mm-dd in client tz
  loading: boolean;
};

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function ymd(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function buildGrid(monthCursor: Date) {
  const first = startOfMonth(monthCursor);
  const startDay = first.getDay();
  const grid: { date: Date; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(first);
    d.setDate(d.getDate() - startDay + i);
    grid.push({ date: d, inMonth: d.getMonth() === monthCursor.getMonth() });
  }
  return grid;
}

export function BookingCalendar({
  monthCursor,
  setMonthCursor,
  selectedDate,
  onSelect,
  availableDays,
  loading,
}: Props) {
  const [focusKey, setFocusKey] = useState<string | null>(selectedDate);
  const cellRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const today = useMemo(() => ymd(new Date()), []);

  const grid = useMemo(() => buildGrid(monthCursor), [monthCursor]);
  const monthLabel = monthCursor.toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();

  useEffect(() => {
    if (focusKey && cellRefs.current[focusKey]) {
      cellRefs.current[focusKey]?.focus();
    }
  }, [focusKey, monthCursor]);

  function shift(direction: -1 | 1) {
    setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + direction, 1));
  }

  function moveFocus(currentKey: string, deltaDays: number) {
    const parts = currentKey.split('-').map(Number);
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    d.setDate(d.getDate() + deltaDays);
    if (d.getMonth() !== monthCursor.getMonth()) {
      setMonthCursor(new Date(d.getFullYear(), d.getMonth(), 1));
    }
    setFocusKey(ymd(d));
  }

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-4">
        <span
          className="font-mono"
          style={{
            color: 'var(--text-muted)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.18em',
          }}
        >
          {monthLabel}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => shift(-1)}
            aria-label="Previous month"
            className="inline-flex items-center justify-center transition-colors"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setMonthCursor(startOfMonth(new Date()))}
            className="font-mono"
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
              background: 'transparent',
            }}
          >
            TODAY
          </button>
          <button
            type="button"
            onClick={() => shift(1)}
            aria-label="Next month"
            className="inline-flex items-center justify-center transition-colors"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>

      <div
        role="grid"
        aria-label="Pick a date"
        className="grid grid-cols-7"
        style={{ gap: 4 }}
      >
        {WEEKDAYS.map((w, i) => (
          <div
            key={`wk-${i}`}
            className="font-mono text-center"
            style={{
              color: 'var(--text-caption)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.12em',
              padding: '8px 0',
            }}
          >
            {w}
          </div>
        ))}
        {grid.map(({ date, inMonth }) => {
          const key = ymd(date);
          const isToday = key === today;
          const isAvailable = availableDays.has(key);
          const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
          const selected = key === selectedDate;
          const disabled = !inMonth || isPast || !isAvailable;
          return (
            <button
              key={key}
              type="button"
              ref={(el) => {
                cellRefs.current[key] = el;
              }}
              role="gridcell"
              aria-disabled={disabled}
              aria-selected={selected}
              tabIndex={focusKey === key ? 0 : -1}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onSelect(key);
                setFocusKey(key);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  moveFocus(key, -1);
                } else if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  moveFocus(key, 1);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  moveFocus(key, -7);
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  moveFocus(key, 7);
                } else if (e.key === 'PageUp') {
                  e.preventDefault();
                  shift(-1);
                } else if (e.key === 'PageDown') {
                  e.preventDefault();
                  shift(1);
                }
              }}
              className="relative inline-flex items-center justify-center transition-all book-daycell"
              style={{
                width: '100%',
                aspectRatio: '1 / 1',
                maxHeight: 56,
                borderRadius: 'var(--radius-md)',
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                fontWeight: 600,
                background: selected
                  ? 'linear-gradient(135deg, var(--accent), var(--indigo))'
                  : 'transparent',
                color: selected
                  ? 'var(--bg-primary)'
                  : !inMonth
                  ? 'var(--text-caption)'
                  : disabled
                  ? 'var(--text-caption)'
                  : 'var(--text-primary)',
                opacity: !inMonth ? 0.45 : disabled ? 0.45 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
                border: selected
                  ? '1px solid transparent'
                  : isToday
                  ? '1px solid var(--border-active)'
                  : '1px solid transparent',
                boxShadow: selected
                  ? '0 0 24px rgba(212,165,116,0.25)'
                  : 'none',
              }}
            >
              {date.getDate()}
              {isAvailable && !disabled && !selected && inMonth && !isPast && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    bottom: 6,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: 'var(--accent)',
                    boxShadow: '0 0 6px var(--accent-glow)',
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
